import React, { useEffect } from 'react';
import {
  useLocation,
  useParams,
  Outlet,
  useMatches,
} from 'react-router-dom';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { useTranslation } from 'react-i18next';

function AnalyticsLayout() {
  const location = useLocation();
  const routeParams = useParams();
  const matches = useMatches();
  const { i18n } = useTranslation();
  const { lang, courseSlug, lessonSlug } = routeParams;

  // Keep the rendered language in sync with the URL's :lang. This runs in
  // an effect (not during render) because triggering i18n.changeLanguage
  // for a language other than i18next's init lng/fallbackLng doesn't
  // reliably resolve within the same render pass in production builds -
  // gating rendering on it (as a previous version of this file did) left
  // the page blank forever for any language besides those two.
  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  useEffect(() => {
    const htmlElement = document.documentElement;
    if (htmlElement.getAttribute('lang') !== i18n.language) {
      htmlElement.setAttribute('lang', i18n.language);
    }
    const direction = i18n.dir();
    if (htmlElement.getAttribute('dir') !== direction) {
      htmlElement.setAttribute('dir', direction);
    }
  }, [i18n, i18n.language]);

  useEffect(() => {
    const eventParams = {
      screen_name: location.pathname,
      firebase_screen_name: location.pathname
    };
    if (courseSlug) {
      eventParams.course = courseSlug;
    }
    if (lessonSlug) {
      eventParams.lesson = lessonSlug.substring(lessonSlug.indexOf('-') + 1);
    }

    // match current location against routes
    if (matches && matches.length) {
      const { handle } = matches.slice(-1)[0]; // get last element, which is the final match

      // set screen name from `handle` on route
      if (handle) {
        eventParams.screen_name = handle;
        eventParams.firebase_screen_name = handle;
      }
    }
    logEvent(getAnalytics(), 'screen_view', eventParams);
  }, [location, courseSlug, lessonSlug, matches]);

  return <Outlet />;
}

export default AnalyticsLayout;
